import { useCallback, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { FilterTableType, TableFilter } from '@trnsact/trnsact-shared-types';
import { Nullable } from 'global';
import { useNotifications } from 'modules/notification';
import { GetTableFiltersResponse, GetTableFiltersVariables } from 'modules/tableFilter';

import { closedLostReportApi } from '../api';
import { ClosedLostFiltersState } from '../types';

type SavedViewDto = Pick<TableFilter, 'name' | 'config' | 'isDefault' | 'tableType'>;

export function useClosedLostSavedViews() {
  const client = useApolloClient();
  const { showNotification } = useNotifications();

  const [views, setViews] = useState<TableFilter[]>([]);
  const [defaultView, setDefaultView] = useState<Nullable<TableFilter>>(null);

  const { loading: isViewsLoading } = useQuery<GetTableFiltersResponse, GetTableFiltersVariables>(
    closedLostReportApi.getTableFilters,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      variables: { tableType: FilterTableType.ClosedLostReport },
      onCompleted(data) {
        const tableFilters = data?.tableFilter?.tableFilters?.data ?? [];
        setViews(tableFilters);
        setDefaultView(tableFilters.find(tf => tf.isDefault) ?? null);
      },
    }
  );

  const [onCreate, { loading: isCreating }] = useMutation(closedLostReportApi.createFilter);
  const [onUpdate, { loading: isUpdating }] = useMutation(closedLostReportApi.updateFilter);
  const [onDelete, { loading: isDeleting }] = useMutation(closedLostReportApi.deleteFilter);

  const refetchViews = useCallback(
    () => client.refetchQueries({ include: [closedLostReportApi.getTableFilters] }),
    [client]
  );

  const handleCreateView = useCallback(
    async (name: string, config: ClosedLostFiltersState) => {
      const dto: SavedViewDto = { name, config, isDefault: false, tableType: FilterTableType.ClosedLostReport };
      const { data } = await onCreate({ variables: { input: dto } });
      if (!data?.tableFilter?.result?.success) {
        showNotification(data?.tableFilter?.result?.error ?? 'View not saved', { type: 'error' });
        return;
      }
      await refetchViews();
      showNotification('View successfully saved!');
    },
    [onCreate, refetchViews, showNotification]
  );

  const handleUpdateView = useCallback(
    async (tableFilterId: string, name: string, config: ClosedLostFiltersState) => {
      const { data } = await onUpdate({ variables: { input: { tableFilterId, name, config } } });
      if (!data?.tableFilter?.result?.success) {
        showNotification(data?.tableFilter?.result?.error ?? 'View not updated', { type: 'error' });
        return;
      }
      await refetchViews();
      showNotification('View successfully updated!');
    },
    [onUpdate, refetchViews, showNotification]
  );

  const handleDeleteView = useCallback(
    async (tableFilterId: string) => {
      const { data } = await onDelete({ variables: { tableFilterId } });
      if (!data?.tableFilter?.result?.success) {
        showNotification(data?.tableFilter?.result?.error ?? 'View not deleted', { type: 'error' });
        return;
      }
      await refetchViews();
      showNotification('View successfully deleted!');
    },
    [onDelete, refetchViews, showNotification]
  );

  return {
    views,
    defaultView,
    isViewsLoading,
    isMutatingView: isCreating || isUpdating || isDeleting,
    handleCreateView,
    handleUpdateView,
    handleDeleteView,
  };
}
